import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users,
  Sparkles,
  PenLine,
  Search,
  CheckCircle2,
  Circle,
  Loader,
  ChevronDown,
  ChevronRight,
  Send,
  X,
  Tag,
  AlertTriangle,
  Clock,
  Zap,
  Globe,
  Lock,
  ExternalLink,
  ClipboardList,
  UserCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase, TABLES } from '../api/db';
import { useAuth } from '../contexts/AuthContext';
import { getAcceptedConnectionsForUser, getDisplayNameForUser } from '../api/connections';
import { generateTaskCode } from '../utils/taskCodeGenerator';
import { createNotification } from '../api/notifications';
import {
  parseTaskFromPrompt,
  fallbackParseFromPrompt,
  isGeminiConfigured,
} from '../api/geminiTaskParser';
import type { GeminiTaskResult } from '../api/geminiTaskParser';
import { TASK_UNIVERSES, TIMELINES } from '../constants/taskCreation';
import type { TaskUniverse, TaskTimeline } from '../constants/taskCreation';
import { normalizeTaskTools } from '../utils/taskRecords';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ConnectedUser {
  userId: string;
  displayName: string;
  sharedTaskCount: number;
  latestActivity: number;
}

interface RecentAssignment {
  id: string;
  title: string;
  taskCode: string;
  assignedUserIds: string[];
  assignedUserNames: string[];
  createdAt: string | null;
  visibility: 'private' | 'public';
}

type Mode = 'ai' | 'manual';
type Phase = 'compose' | 'review' | 'success';

// ─── Constants ────────────────────────────────────────────────────────────────

const EXAMPLE_PROMPTS = [
  'Build a landing page for our new product launch with animations and a waitlist form',
  'Set up a CI/CD pipeline using GitHub Actions to auto-deploy to Firebase on push to main',
  'Design 3 social media post templates for Instagram — tech startup aesthetic, dark theme',
  'Write a Python scraper that pulls competitor pricing daily and saves to a spreadsheet',
  'Create a 60-second promo video script and edit a short reel from our existing footage',
];

const UNIVERSE_COLORS: Record<string, string> = {
  build: 'from-cyan-500 to-blue-600',
  design: 'from-purple-500 to-pink-600',
  create: 'from-orange-500 to-red-500',
  automate: 'from-yellow-500 to-amber-600',
  manage: 'from-teal-500 to-emerald-600',
  market: 'from-green-500 to-lime-600',
  learn: 'from-blue-500 to-indigo-600',
  produce: 'from-pink-500 to-rose-600',
  research: 'from-violet-500 to-purple-600',
  brand: 'from-fuchsia-500 to-purple-500',
  entertain: 'from-red-500 to-pink-500',
  other: 'from-slate-500 to-slate-600',
};

// ─── User selector ────────────────────────────────────────────────────────────

function UserSelector({
  users,
  selectedIds,
  onToggle,
  search,
  onSearchChange,
  loading,
}: {
  users: ConnectedUser[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  search: string;
  onSearchChange: (v: string) => void;
  loading: boolean;
}) {
  const filtered = useMemo(
    () =>
      users.filter((u) =>
        u.displayName.toLowerCase().includes(search.toLowerCase())
      ),
    [users, search]
  );

  return (
    <aside className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-tech-border/40 shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-lg bg-tech-cyan/10 border border-tech-cyan/20">
            <Users className="w-4 h-4 text-tech-cyan" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-tech-text">Assign To</h2>
            <p className="text-[10px] text-tech-text-muted">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select people'}
            </p>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-tech-text-muted/60" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search connections…"
            className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-tech-dark border border-tech-border/60 text-tech-text text-xs placeholder-tech-text-muted/50 focus:outline-none focus:border-tech-cyan/50"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto custom-scrollbar py-1">
        {loading ? (
          <div className="flex items-center justify-center py-10 gap-2 text-tech-text-muted">
            <Loader className="w-4 h-4 animate-spin" />
            <span className="text-xs">Loading…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 px-4">
            <UserCheck className="w-8 h-8 text-tech-text-muted/20 mx-auto mb-2" />
            <p className="text-xs text-tech-text-muted">
              {users.length === 0
                ? 'No connections yet. Connect with people in the Marketplace first.'
                : 'No matches found.'}
            </p>
            {users.length === 0 && (
              <Link
                to="/marketplace"
                className="inline-block mt-3 text-xs text-tech-cyan hover:underline"
              >
                Browse Marketplace →
              </Link>
            )}
          </div>
        ) : (
          filtered.map((user) => {
            const selected = selectedIds.has(user.userId);
            return (
              <button
                key={user.userId}
                type="button"
                onClick={() => onToggle(user.userId)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b border-tech-border/20 ${
                  selected
                    ? 'bg-tech-cyan/8 hover:bg-tech-cyan/12'
                    : 'hover:bg-white/[0.03]'
                }`}
              >
                {/* Avatar */}
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 transition-all ${
                    selected
                      ? 'bg-gradient-to-tr from-tech-cyan to-blue-500 ring-2 ring-tech-cyan/50'
                      : 'bg-gradient-to-tr from-slate-600 to-slate-500'
                  }`}
                >
                  {user.displayName.charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${selected ? 'text-tech-cyan' : 'text-tech-text'}`}>
                    {user.displayName}
                  </p>
                  <p className="text-[10px] text-tech-text-muted">
                    {user.sharedTaskCount} shared task{user.sharedTaskCount !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Checkbox */}
                <div className={`shrink-0 transition-colors ${selected ? 'text-tech-cyan' : 'text-tech-text-muted/30'}`}>
                  {selected ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Selected footer */}
      {selectedIds.size > 0 && (
        <div className="shrink-0 px-4 py-2.5 border-t border-tech-border/40 bg-tech-cyan/5">
          <p className="text-xs font-semibold text-tech-cyan">
            {selectedIds.size} {selectedIds.size === 1 ? 'person' : 'people'} selected
          </p>
          <p className="text-[10px] text-tech-text-muted mt-0.5">
            They'll be notified and added to the workspace.
          </p>
        </div>
      )}
    </aside>
  );
}

// ─── Review field card ────────────────────────────────────────────────────────

function ReviewField({
  label,
  value,
  onChange,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-bold uppercase tracking-wider text-tech-text-muted">
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 rounded-lg bg-tech-dark border border-tech-border text-tech-text text-sm resize-none focus:outline-none focus:border-tech-cyan/50"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-tech-dark border border-tech-border text-tech-text text-sm focus:outline-none focus:border-tech-cyan/50"
        />
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const TaskAssign: React.FC = () => {
  const { currentUser } = useAuth();

  // ── Connected users ──
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [userSearch, setUserSearch] = useState('');

  // ── Composer state ──
  const [mode, setMode] = useState<Mode>('ai');
  const [phase, setPhase] = useState<Phase>('compose');

  // AI mode
  const [prompt, setPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [parsedTask, setParsedTask] = useState<GeminiTaskResult | null>(null);

  // Editable review fields (seeded from AI or manual)
  const [title, setTitle] = useState('');
  const [goal, setGoal] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [universe, setUniverse] = useState<TaskUniverse>('build');
  const [timeline, setTimeline] = useState<TaskTimeline>('this-week');
  const [visibility, setVisibility] = useState<'private' | 'public'>('private');

  // ── Submission ──
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successTask, setSuccessTask] = useState<{ id: string; taskCode: string; title: string } | null>(null);

  // ── Recent assignments ──
  const [recentAssignments, setRecentAssignments] = useState<RecentAssignment[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentExpanded, setRecentExpanded] = useState(false);

  // ── Mobile tab ──
  const [mobileTab, setMobileTab] = useState<'people' | 'create' | 'history'>('create');

  // ── Load connected users ──
  useEffect(() => {
    if (!currentUser) return;
    setUsersLoading(true);

    getAcceptedConnectionsForUser(currentUser.uid).then(async (connections) => {
      // Deduplicate by other user id
      const userMap = new Map<string, { count: number; latest: number }>();
      connections.forEach((c) => {
        const otherId = c.taskerId === currentUser.uid ? c.taskOwnerId : c.taskerId;
        const ts = c.updatedAt ? new Date(c.updatedAt).getTime() : 0;
        const existing = userMap.get(otherId);
        if (existing) {
          existing.count++;
          if (ts > existing.latest) existing.latest = ts;
        } else {
          userMap.set(otherId, { count: 1, latest: ts });
        }
      });

      const enriched = await Promise.all(
        Array.from(userMap.entries()).map(async ([userId, { count, latest }]) => {
          const displayName = await getDisplayNameForUser(userId, 'Unknown');
          return { userId, displayName, sharedTaskCount: count, latestActivity: latest } as ConnectedUser;
        })
      );

      setConnectedUsers(enriched.sort((a, b) => b.latestActivity - a.latestActivity));
      setUsersLoading(false);
    }).catch((err) => {
      console.error('Error loading connections:', err);
      setUsersLoading(false);
    });
  }, [currentUser]);

  // ── Load recent assignments ──
  useEffect(() => {
    if (!currentUser) return;
    setRecentLoading(true);

    (async () => {
      try {
        const { data: rows } = await supabase
          .from(TABLES.TASK_HATCH_SUBMISSIONS)
          .select('*')
          .eq('user_id', currentUser.uid)
          .eq('source', 'direct-assign')
          .order('created_at', { ascending: false })
          .limit(10);
        const tasks: RecentAssignment[] = await Promise.all(
          (rows ?? []).map(async (r) => {
            const row = r as Record<string, unknown>;
            const assignedIds: string[] = (row['assigned_users'] as string[]) || [];
            const names = await Promise.all(
              assignedIds.map((id) => getDisplayNameForUser(id, 'Unknown'))
            );
            return {
              id: row['id'] as string,
              title: (row['title'] as string) || 'Untitled',
              taskCode: (row['task_code'] as string) || '',
              assignedUserIds: assignedIds,
              assignedUserNames: names,
              createdAt: (row['created_at'] as string) || null,
              visibility: ((row['visibility'] as string) === 'public' ? 'public' : 'private') as 'public' | 'private',
            };
          })
        );
        setRecentAssignments(tasks);
      } catch (err) {
        console.error('Error loading recent assignments:', err);
      } finally {
        setRecentLoading(false);
      }
    })();
  }, [currentUser, successTask]);

  // ── User toggle ──
  const toggleUser = useCallback((userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  // ── AI parse ──
  const handleAiParse = async () => {
    if (!prompt.trim()) return;
    setAiLoading(true);
    setAiError('');
    try {
      let result: GeminiTaskResult;
      try {
        result = await parseTaskFromPrompt(prompt);
      } catch {
        result = fallbackParseFromPrompt(prompt);
        setAiError('AI unavailable — auto-filled from your prompt. Edit below.');
      }
      setParsedTask(result);
      setTitle(result.title);
      setGoal(result.goal);
      setDescription(result.description);
      setTags(result.tags);
      setUniverse(result.universe);
      setTimeline(result.timeline);
      setPhase('review');
    } catch (err) {
      setAiError('Failed to parse prompt. Try again or use manual mode.');
    } finally {
      setAiLoading(false);
    }
  };

  // ── Add tag ──
  const addTag = (t: string) => {
    const clean = t.trim().toLowerCase().replace(/\s+/g, '-');
    if (clean && !tags.includes(clean) && tags.length < 8) {
      setTags((prev) => [...prev, clean]);
    }
    setTagInput('');
  };

  // ── Reset ──
  const resetComposer = () => {
    setPhase('compose');
    setPrompt('');
    setTitle('');
    setGoal('');
    setDescription('');
    setTags([]);
    setTagInput('');
    setUniverse('build');
    setTimeline('this-week');
    setParsedTask(null);
    setAiError('');
    setSubmitError('');
    setSuccessTask(null);
    setSelectedIds(new Set());
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!currentUser || !title.trim() || selectedIds.size === 0) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const taskCode = generateTaskCode();
      const assignedUserIds = Array.from(selectedIds);

      const now = new Date().toISOString();
      const { data: inserted, error: insertErr } = await supabase
        .from(TABLES.TASK_HATCH_SUBMISSIONS)
        .insert({
          task_code: taskCode,
          title: title.trim(),
          goal: goal.trim(),
          summary: goal.trim(),
          description: description.trim(),
          requirements: description.trim(),
          acceptance_criteria: goal.trim(),
          tags,
          universe,
          timeline,
          visibility,
          status: 'in-progress',
          source: 'direct-assign',
          user_id: currentUser.uid,
          user_email: currentUser.email || '',
          user_name: currentUser.displayName || 'Owner',
          assigned_users: assignedUserIds,
          collab_style: 'team',
          experience: 'maker',
          difficulty: 'intermediate',
          tools: normalizeTaskTools(parsedTask?.suggestedTools ?? []),
          ai_tools_used: parsedTask?.suggestedTools ?? [],
          created_at: now,
          updated_at: now,
        })
        .select('id')
        .single();
      if (insertErr) throw insertErr;

      const taskId = (inserted as { id: string }).id;

      // Add each assigned user as a task participant
      await Promise.all(
        assignedUserIds.map((uid) =>
          supabase.from(TABLES.TASK_PARTICIPANTS).upsert({
            task_id: taskId,
            user_id: uid,
            added_at: now,
            role: 'collaborator',
          })
        )
      );

      // Notify each assigned user
      const ownerName = currentUser.displayName || 'Someone';
      await Promise.all(
        assignedUserIds.map((uid) =>
          createNotification(
            uid,
            'task_assigned',
            `You've been assigned to "${title.trim()}"`,
            `${ownerName} has directly assigned you to a new task. Open the workspace to get started.`,
            {
              actionUrl: `/tasks/${taskId}`,
              actionLabel: 'Open Task',
              relatedTaskId: taskId,
              relatedUserId: currentUser.uid,
              priority: 'high',
            }
          ).catch(console.error)
        )
      );

      setSuccessTask({ id: taskId, taskCode, title: title.trim() });
      setPhase('success');
    } catch (err) {
      console.error('Error creating assignment:', err);
      setSubmitError('Failed to create task. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const geminiConfigured = isGeminiConfigured();
  const canSubmit = title.trim().length > 0 && selectedIds.size > 0 && !submitting;

  // ────────────────────────────────────────────────────────────────────────────
  // Render — all panels inlined to prevent focus loss on re-render
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden bg-[#080c14]">
      {/* ── Page header ── */}
      <div className="shrink-0 border-b border-tech-border/40 bg-tech-surface/90 backdrop-blur-sm px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-violet-500/20 shrink-0">
            <Zap className="h-4.5 w-4.5 text-violet-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-lg font-bold tracking-tight text-white">TaskAssign</h1>
            <p className="text-xs text-tech-text-muted truncate">
              Create tasks and assign them directly to your connections
            </p>
          </div>
          {recentAssignments.length > 0 && (
            <span className="shrink-0 text-[10px] font-semibold px-2 py-1 rounded-lg bg-tech-cyan/10 border border-tech-cyan/20 text-tech-cyan">
              {recentAssignments.length} assigned
            </span>
          )}
        </div>
      </div>

      {/* ── Mobile tab bar ── */}
      <div className="flex lg:hidden border-b border-tech-border/40 bg-tech-dark/50 shrink-0">
        {([
          { id: 'create' as const, label: 'Create', icon: Sparkles },
          { id: 'people' as const, label: 'Assign To', icon: Users },
          { id: 'history' as const, label: 'History', icon: ClipboardList },
        ]).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setMobileTab(id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold transition-colors ${
              mobileTab === id
                ? 'text-tech-cyan border-b-2 border-tech-cyan bg-tech-cyan/5'
                : 'text-tech-text-muted hover:text-tech-text'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Main body ── */}
      <div className="flex-1 flex min-h-0">
        {/* Left: user selector — hidden on mobile unless people tab */}
        <div className={`${mobileTab !== 'people' ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-72 xl:w-80 shrink-0 border-r border-tech-border/40 bg-tech-dark/30`}>
          <UserSelector
            users={connectedUsers}
            selectedIds={selectedIds}
            onToggle={toggleUser}
            search={userSearch}
            onSearchChange={setUserSearch}
            loading={usersLoading}
          />
        </div>

        {/* Right: composer + recent */}
        <div className={`${mobileTab === 'people' ? 'hidden lg:flex' : mobileTab === 'history' ? 'hidden lg:flex' : 'flex'} flex-col flex-1 min-w-0`}>

          {/* ── Composer area (inlined — no inner components to avoid focus loss) ── */}
          <div className="flex-1 min-h-0 flex flex-col">

            {/* SUCCESS */}
            {phase === 'success' && successTask && (
              <div className="flex flex-col items-center justify-center h-full text-center px-8 py-12 gap-4">
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-2">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-tech-text">Task assigned!</h3>
                <p className="text-sm text-tech-text-muted max-w-xs">
                  <span className="font-mono text-tech-cyan text-xs">{successTask.taskCode}</span>
                  {' '}— <strong className="text-tech-text">{successTask.title}</strong> has been created
                  and <strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? 'person has' : 'people have'} been notified.
                </p>
                <div className="flex gap-3 mt-2">
                  <Link
                    to={`/tasks/${successTask.id}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-tech-dark"
                    style={{ background: 'linear-gradient(135deg, #00ff88, #00cc6a)' }}
                  >
                    <ExternalLink className="w-4 h-4" /> Open Workspace
                  </Link>
                  <button
                    type="button"
                    onClick={resetComposer}
                    className="px-4 py-2 rounded-xl text-sm font-semibold border border-tech-border text-tech-text hover:bg-white/5 transition-colors"
                  >
                    Assign Another
                  </button>
                </div>
              </div>
            )}

            {/* AI COMPOSE */}
            {phase !== 'success' && mode === 'ai' && phase === 'compose' && (
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-5 space-y-5">
                  {/* Mode toggle */}
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setMode('ai')}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border bg-violet-500/15 border-violet-500/40 text-violet-300">
                      <Sparkles className="w-3.5 h-3.5" /> AI Prompt
                    </button>
                    <button type="button" onClick={() => { setMode('manual'); setPhase('review'); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-tech-border text-tech-text-muted hover:text-tech-text transition-colors">
                      <PenLine className="w-3.5 h-3.5" /> Manual
                    </button>
                  </div>

                  {!geminiConfigured && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>Gemini API key not configured — AI will auto-fill from your prompt text instead.</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-tech-text-muted uppercase tracking-wider">Describe the task</label>
                    <div className="relative">
                      <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleAiParse(); } }}
                        placeholder="e.g. Build a dashboard that shows real-time server metrics using React and Firebase…"
                        rows={5}
                        className="w-full px-4 py-3 rounded-xl bg-[#0a0e1a] border border-tech-border/60 text-tech-text text-sm placeholder-tech-text-muted/50 resize-none focus:outline-none focus:border-violet-500/50 font-mono leading-relaxed"
                      />
                      <div className="absolute bottom-2 right-3 text-[10px] text-tech-text-muted/50">Ctrl+Enter to parse</div>
                    </div>
                    {aiError && (
                      <p className="text-xs text-amber-400 flex items-center gap-1.5">
                        <AlertTriangle className="w-3 h-3 shrink-0" />{aiError}
                      </p>
                    )}
                  </div>

                  <div>
                    <p className="text-[10px] text-tech-text-muted/70 mb-2 uppercase tracking-wider font-semibold">Examples</p>
                    <div className="flex flex-wrap gap-1.5">
                      {EXAMPLE_PROMPTS.map((ex) => (
                        <button key={ex} type="button" onClick={() => setPrompt(ex)}
                          className="px-2.5 py-1 rounded-lg text-[11px] text-tech-text-muted border border-tech-border/50 hover:border-tech-border hover:text-tech-text transition-colors text-left">
                          {ex.slice(0, 50)}…
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="shrink-0 px-6 py-4 border-t border-tech-border/40 flex items-center justify-between gap-3">
                  <div className="text-xs text-tech-text-muted">
                    {selectedIds.size === 0
                      ? <span className="text-amber-400">⚠ Select people to assign</span>
                      : <span className="text-emerald-400">✓ {selectedIds.size} selected</span>}
                  </div>
                  <button type="button" onClick={handleAiParse} disabled={!prompt.trim() || aiLoading}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-40 transition-opacity"
                    style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}>
                    {aiLoading ? <><Loader className="w-4 h-4 animate-spin" /><span>Parsing<span className="animate-pulse">…</span></span></>
                      : <><Sparkles className="w-4 h-4" />Parse with AI</>}
                  </button>
                </div>
              </div>
            )}

            {/* REVIEW / MANUAL */}
            {phase !== 'success' && !(mode === 'ai' && phase === 'compose') && (
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-5 space-y-4">
                  {/* Header row */}
                  <div className="flex items-center justify-between">
                    {mode === 'ai' && (
                      <button type="button" onClick={() => setPhase('compose')}
                        className="text-xs text-tech-text-muted hover:text-tech-text flex items-center gap-1 transition-colors">
                        ← Back to prompt
                      </button>
                    )}
                    <div className="flex gap-2 ml-auto">
                      <button type="button" onClick={() => { setMode('ai'); setPhase('compose'); }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${mode === 'ai' ? 'bg-violet-500/15 border-violet-500/40 text-violet-300' : 'border-tech-border text-tech-text-muted hover:text-tech-text'}`}>
                        <Sparkles className="w-3 h-3" /> AI
                      </button>
                      <button type="button" onClick={() => setMode('manual')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${mode === 'manual' ? 'bg-tech-cyan/15 border-tech-cyan/40 text-tech-cyan' : 'border-tech-border text-tech-text-muted hover:text-tech-text'}`}>
                        <PenLine className="w-3 h-3" /> Manual
                      </button>
                    </div>
                  </div>

                  {aiError && mode === 'ai' && (
                    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{aiError}
                    </div>
                  )}

                  <ReviewField label="Task Title *" value={title} onChange={setTitle} />
                  <ReviewField label="Goal / Outcome" value={goal} onChange={setGoal} />
                  <ReviewField label="Description" value={description} onChange={setDescription} multiline />

                  {/* Universe */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-tech-text-muted">Category</label>
                    <div className="flex flex-wrap gap-1.5">
                      {TASK_UNIVERSES.map((u) => (
                        <button key={u.id} type="button" onClick={() => setUniverse(u.id)}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border transition-colors ${universe === u.id ? 'bg-tech-cyan/15 border-tech-cyan/40 text-tech-cyan font-semibold' : 'border-tech-border text-tech-text-muted hover:text-tech-text'}`}>
                          <span>{u.emoji}</span>{u.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-tech-text-muted">Timeline</label>
                    <div className="flex flex-wrap gap-1.5">
                      {TIMELINES.map((t) => (
                        <button key={t.id} type="button" onClick={() => setTimeline(t.id as TaskTimeline)}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border transition-colors ${timeline === t.id ? 'bg-tech-cyan/15 border-tech-cyan/40 text-tech-cyan font-semibold' : 'border-tech-border text-tech-text-muted hover:text-tech-text'}`}>
                          <Clock className="w-3 h-3" />{t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-tech-text-muted">Tags</label>
                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                      {tags.map((tag) => (
                        <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-tech-cyan/10 border border-tech-cyan/20 text-tech-cyan text-xs">
                          <Tag className="w-2.5 h-2.5" />{tag}
                          <button type="button" onClick={() => setTags((prev) => prev.filter((t) => t !== tag))} className="hover:text-white ml-0.5">
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); } }}
                      placeholder="Add tag, press Enter…"
                      className="w-full px-3 py-2 rounded-lg bg-tech-dark border border-tech-border text-tech-text text-sm focus:outline-none focus:border-tech-cyan/50"
                    />
                  </div>

                  {/* Visibility */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-tech-text-muted">Visibility</label>
                    <div className="flex gap-2">
                      {([
                        { id: 'private' as const, label: 'Private', icon: Lock, hint: 'Only assigned people can see' },
                        { id: 'public' as const, label: 'Public', icon: Globe, hint: 'Visible in Marketplace too' },
                      ]).map(({ id, label, icon: Icon, hint }) => (
                        <button key={id} type="button" onClick={() => setVisibility(id)}
                          className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${visibility === id ? (id === 'private' ? 'bg-slate-700/40 border-slate-500/50 text-slate-300' : 'bg-tech-cyan/10 border-tech-cyan/30 text-tech-cyan') : 'border-tech-border text-tech-text-muted hover:text-tech-text'}`}>
                          <Icon className="w-4 h-4 shrink-0" />
                          <div className="text-left">
                            <p className="text-xs font-semibold">{label}</p>
                            <p className="text-[10px] opacity-70">{hint}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {submitError && (
                    <p className="text-xs text-red-400 flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3" />{submitError}
                    </p>
                  )}
                </div>

                <div className="shrink-0 px-6 py-4 border-t border-tech-border/40 flex items-center justify-between gap-3">
                  <div className="text-xs text-tech-text-muted min-w-0">
                    {selectedIds.size === 0
                      ? <span className="text-amber-400">⚠ Select people first</span>
                      : !title.trim()
                        ? <span className="text-amber-400">⚠ Title required</span>
                        : <span className="text-emerald-400">✓ Ready to assign to {selectedIds.size} {selectedIds.size === 1 ? 'person' : 'people'}</span>}
                  </div>
                  <button type="button" onClick={handleSubmit} disabled={!canSubmit}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-tech-dark disabled:opacity-40 transition-opacity shrink-0"
                    style={{ background: 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)' }}>
                    {submitting ? <Loader className="w-4 h-4 animate-spin text-white" /> : <Send className="w-4 h-4" />}
                    Assign Task
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Recent assignments (desktop) ── */}
          <div className="hidden lg:block shrink-0 border-t border-tech-border/40 bg-[#080c14]">
            <button type="button" onClick={() => setRecentExpanded((v) => !v)}
              className="w-full flex items-center gap-2 px-6 py-3 hover:bg-white/[0.02] transition-colors">
              <ClipboardList className="w-4 h-4 text-tech-text-muted" />
              <span className="text-xs font-semibold text-tech-text-muted uppercase tracking-wider">Recent Assignments</span>
              <span className="ml-1 text-[10px] text-tech-text-muted/60">({recentAssignments.length})</span>
              <div className="ml-auto">
                {recentExpanded ? <ChevronDown className="w-4 h-4 text-tech-text-muted" /> : <ChevronRight className="w-4 h-4 text-tech-text-muted" />}
              </div>
            </button>
            {recentExpanded && (
              <div className="px-6 pb-4 space-y-2">
                {recentLoading
                  ? <div className="flex items-center gap-2 py-4 text-tech-text-muted"><Loader className="w-3.5 h-3.5 animate-spin" /><span className="text-xs">Loading…</span></div>
                  : recentAssignments.length === 0
                    ? <p className="text-xs text-tech-text-muted py-3">No assignments yet. Create your first one above.</p>
                    : recentAssignments.map((task) => (
                      <div key={task.id} className="flex items-center gap-3 p-3 rounded-xl bg-tech-gray/20 border border-tech-border/30 hover:border-tech-border/60 transition-colors">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${UNIVERSE_COLORS['other']} flex items-center justify-center shrink-0`}>
                          <ClipboardList className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-tech-text truncate">{task.title}</p>
                            {task.visibility === 'private' && <Lock className="w-3 h-3 text-tech-text-muted/50 shrink-0" />}
                          </div>
                          <p className="text-[10px] text-tech-text-muted font-mono">{task.taskCode}</p>
                          <p className="text-[10px] text-tech-text-muted/70 truncate">→ {task.assignedUserNames.join(', ')}</p>
                        </div>
                        <Link to={`/tasks/${task.id}`} className="shrink-0 p-1.5 rounded-lg text-tech-text-muted hover:text-tech-cyan hover:bg-white/5 transition-colors" title="Open workspace">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile: history tab */}
        {mobileTab === 'history' && (
          <div className="flex flex-col flex-1 min-w-0 lg:hidden overflow-y-auto">
            <div className="p-4 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-tech-text-muted">Recent Assignments</p>
              {recentLoading ? (
                <div className="flex items-center gap-2 py-6 text-tech-text-muted justify-center">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading…</span>
                </div>
              ) : recentAssignments.length === 0 ? (
                <div className="text-center py-10">
                  <ClipboardList className="w-8 h-8 text-tech-text-muted/20 mx-auto mb-2" />
                  <p className="text-sm text-tech-text-muted">No assignments yet.</p>
                </div>
              ) : (
                recentAssignments.map((task) => (
                  <div key={task.id} className="flex items-center gap-3 p-3 rounded-xl bg-tech-gray/20 border border-tech-border/30">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-tech-text truncate">{task.title}</p>
                      <p className="text-[10px] font-mono text-tech-text-muted">{task.taskCode}</p>
                      <p className="text-[10px] text-tech-text-muted/70 truncate">→ {task.assignedUserNames.join(', ')}</p>
                    </div>
                    <Link to={`/tasks/${task.id}`} className="shrink-0 p-2 rounded-lg text-tech-text-muted hover:text-tech-cyan transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskAssign;
